import React from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Heading,
  IconButton,
  Paragraph,
  Popover,
  Spacer,
  Table,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconEditFilled, IconInfoFilled } from '@zillow/constellation-icons';
import {
  MAX_NUMBER_OF_APPLICATION_USERS,
  PERMISSION_STATUS,
  RentalManagerUserManagementCompanyWebApiHandler_cancelUserInvitation,
  RentalManagerUserManagementCompanyWebApiHandler_deactivateUser,
  stringHelper,
  useRentalManagerUser,
  useRmwPaths,
  useRouter,
  UserPermissionsConstants,
} from '@zg-rentals/rental-manager-env';
import type { Property } from '@zg-rentals/rental-manager-env';
import { Col, HR, Row } from '@zg-rentals/zrs-comet';
import { StyledIconCheckmark, StyledIconClose, styledPreviewPropertyTableContainerStyles } from './StyledComponents';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable, AdvancedTableStyles } from '@zg-rentals/advanced-table';
import { EVENT_IDS } from '../../modal/ClickstreamEvents';
import { ConfirmationModal } from '@zg-rentals/rental-revenue-components';

export interface PreviewProps {
  onSendInvite: () => void;
  onBack: () => void;
  onEditRole: () => void;
  onEditAllProperties: () => void;
  onEditProperties: () => void;
  onEditPermissions: () => void;
  onCancel: () => void;
}

const displayedPermissions = Object.keys(UserPermissionsConstants.PERMISSIONS).reduce(
  (accumulator: { [key: string]: string }, permissionKey: string) => {
    const hiddenPermissions = [
      UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN,
      UserPermissionsConstants.PERMISSIONS.COMPANY_USER,
      UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES,
    ];
    const permissionsKeyTyped = permissionKey as keyof typeof UserPermissionsConstants.PERMISSIONS;

    if (!hiddenPermissions.includes(UserPermissionsConstants.PERMISSIONS[permissionsKeyTyped])) {
      accumulator[permissionsKeyTyped] = UserPermissionsConstants.PERMISSIONS[permissionsKeyTyped];
    }
    return accumulator;
  },
  {},
);

const Preview = ({
  onSendInvite,
  onBack,
  onEditRole,
  onEditAllProperties,
  onEditProperties,
  onEditPermissions,
  onCancel,
}: PreviewProps) => {
  const [error, setError] = React.useState('');
  const paths = useRmwPaths();
  const [confirmationModalData, setConfirmationModalData] = React.useState({
    display: false,
    textContent: '',
    header: '',
    onSubmit: () => undefined,
  });
  const { companyAdmins } = useApplicationPermissions();
  // @ts-expect-error Redux slice typing differs in this app
  const currentUser = useSelector((state) => state.userManagement.currentUser);
  const { user } = useRentalManagerUser();

  const canAccessBusinessVerificationLite = user.features.canAccessBusinessVerificationLite;
  const isAdmin = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN];
  const isCompanyAdminWithApplicationPermissions = companyAdmins.find(
    (companyAdmin) =>
      companyAdmin.permissionStatus === PERMISSION_STATUS.granted && companyAdmin.userToken === currentUser.userToken,
  );
  const allProperties = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES] || isAdmin;
  const { isInvitation, isPending, isApplicationManager } = currentUser;
  const isYou = !isInvitation && !isPending && user.userToken === currentUser.userToken;
  const hasApplicationPermissions = isCompanyAdminWithApplicationPermissions || isApplicationManager;
  const router = useRouter();

  const handleCancelInvitation = () => {
    setConfirmationModalData({
      display: true,
      textContent: 'Are you sure you want to cancel this invite?',
      header: 'Cancel invite',
      onSubmit: () => {
        RentalManagerUserManagementCompanyWebApiHandler_cancelUserInvitation({ email: currentUser.email })
          .then(() => {
            router.push(`${paths.userManagementPage}/${paths.userManagementPending}`);
            setConfirmationModalData({ ...confirmationModalData, display: false });
            return;
          })
          .catch(() => {
            setError('Failed to cancel invite.');
          });
      },
    });
  };

  const handleDeactivateAccount = () => {
    setConfirmationModalData({
      display: true,
      textContent: 'Are you sure you want to deactivate this user?',
      header: 'Deactivate user',
      onSubmit: () => {
        RentalManagerUserManagementCompanyWebApiHandler_deactivateUser({
          userBrand: currentUser.userBrand,
          userToken: currentUser.userToken,
        })
          .then(() => {
            setConfirmationModalData({ ...confirmationModalData, display: false });
            router.push(`${paths.userManagementPage}/${paths.userManagementUsers}`);
            return;
          })
          .catch((e: Error) => {
            setError((e && e.message) || 'Failed to remove user, please try again later.');
            setConfirmationModalData({ ...confirmationModalData, display: false });
          });
      },
    });
  };

  const makePermissionTableOptions = (permissionList: { [keyof: string]: string }): Array<[JSX.Element, string]> =>
    Object.keys(permissionList).map((permission: string) => {
      const description =
        UserPermissionsConstants.DESCRIPTION[permission as keyof typeof UserPermissionsConstants.DESCRIPTION]
          .DESCRIPTION;
      const icon = currentUser.permissions[permission] || isAdmin ? <StyledIconCheckmark /> : <StyledIconClose />;
      return [
        <Spacer css={{ marginBlock: 'sm', marginInlineStart: 'sm' }} key={permission}>
          <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Spacer css={{ marginInlineEnd: 'sm' }}>{icon}</Spacer>
            {description}
          </Box>
        </Spacer>,
        permission,
      ];
    });

  const applicationPermissionOption = canAccessBusinessVerificationLite && (
    <Spacer css={{ marginBlock: 'xs', marginInlineStart: 'sm' }}>
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Spacer css={{ marginInlineEnd: 'sm' }}>
          {hasApplicationPermissions ? <StyledIconCheckmark /> : <StyledIconClose />}
        </Spacer>
        {'Review applications, credit report, background check report'}
        <Spacer css={{ margin: 'xs' }}>
          <Popover
            header={<Heading level={6}>Application manager action required</Heading>}
            body={
              <Paragraph>
                The application manager can change user permissions at any time, allowing up to
                {` ${MAX_NUMBER_OF_APPLICATION_USERS} `}
                users on this account to view and accept applications. Only the application manager can assign this
                permission.
              </Paragraph>
            }
            placement="top"
            trigger={
              <IconButton
                emphasis="bare"
                shape="circle"
                size="sm"
                title="More information"
                tone="brand"
                icon={<IconInfoFilled />}
              />
            }
          />
        </Spacer>
      </Box>
    </Spacer>
  );

  let previewProperties = currentUser.properties;
  if (isPending || isInvitation) {
    if (isAdmin || allProperties) {
      previewProperties = currentUser.availableProperties;
    }
  }

  const propertyListConfig: Array<ColumnConfig<Property>> = [
    {
      width: 2,
      header: 'Property Name',
      renderer: ({ name, street }) => name || street,
      align: 'left',
    },
    {
      width: 1,
      header: 'City',
      renderer: ({ city }) => city,
      align: 'center',
    },
    {
      width: 1,
      header: 'State',
      renderer: ({ state }) => state,
      align: 'center',
    },
  ];

  return (
    <div>
      {isInvitation && (
        <Spacer css={{ marginBlock: 'sm' }}>
          <Text asChild>
            <div>{`Please review ${currentUser.userName}'s access to the properties and the features of this company account.`}</div>
          </Text>
        </Spacer>
      )}
      <Row hidden={[]}>
        <Col md={12} lg={6}>
          <div className="AddUserPage-preview-values">
            <Text asChild>
              <div>
                <Text asChild>
                  <b>Email: </b>
                </Text>
                {currentUser.email}
                {currentUser.isPending && (
                  <>
                    <Spacer css={{ marginInline: 'xs' }} asChild>
                      <span>
                        <Tag tone="gray" size="sm">
                          Pending
                        </Tag>
                      </span>
                    </Spacer>
                    <TextButton onClick={handleCancelInvitation}>Cancel Invite</TextButton>
                  </>
                )}
                {isYou && (
                  <Spacer css={{ marginInline: 'xs' }} asChild>
                    <span>
                      <Tag tone="gray" size="sm">
                        You
                      </Tag>
                    </span>
                  </Spacer>
                )}
              </div>
            </Text>
            <Text asChild>
              <div>
                <Text asChild>
                  <b>Name: </b>
                </Text>
                {currentUser.userName}
              </div>
            </Text>
            <Text asChild>
              <div>
                <Text asChild>
                  <b>Role: </b>
                </Text>
                {isAdmin ? 'Company admin' : 'User'}
                {isApplicationManager && ', application manager'}
                {!isYou && (
                  <Spacer css={{ marginInline: 'xs' }} asChild>
                    <span>
                      <TextButton disabled={isApplicationManager} onClick={onEditRole}>
                        Edit
                      </TextButton>
                    </span>
                  </Spacer>
                )}
              </div>
            </Text>
            <Text asChild>
              <div>
                <Text asChild>
                  <b>Access to: </b>
                </Text>
                {allProperties ? 'All company properties' : 'Limited Properties'}
                {!isYou && !isAdmin && (
                  <Spacer css={{ marginInline: 'xs' }} asChild>
                    <span>
                      <TextButton onClick={onEditAllProperties}>Edit</TextButton>
                    </span>
                  </Spacer>
                )}
              </div>
            </Text>
          </div>
        </Col>
      </Row>
      {!allProperties && (
        <Row hidden={[]}>
          <Col md={12} lg={6}>
            {allProperties && (
              <Text>{`${currentUser.userName} will have access to any properties added in the future.`}</Text>
            )}
            <Spacer css={{ marginBlockEnd: 'sm' }}>
              <Text asChild>
                <b>
                  {stringHelper.pluralize(
                    previewProperties.length,
                    isInvitation || isPending ? 'Additional property' : 'Property',
                    isInvitation || isPending ? 'Additional properties' : 'Properties',
                  )}
                </b>
              </Text>
              <Spacer css={{ marginInline: 'xs' }} asChild>
                <span>{!isYou && !allProperties && <TextButton onClick={onEditProperties}>Edit</TextButton>}</span>
              </Spacer>
            </Spacer>
            <Box className={styledPreviewPropertyTableContainerStyles}>
              {previewProperties.length ? (
                <AdvancedTable
                  columnConfig={propertyListConfig}
                  data={previewProperties}
                  tableStyle={AdvancedTableStyles.stripes}
                  stickyHeader={true}
                />
              ) : (
                <></>
              )}
            </Box>
            <Spacer css={{ marginBlockEnd: 'lg' }} />
          </Col>
        </Row>
      )}
      <Row hidden={[]}>
        <Col md={6} sm={12}>
          <Table.Root aria-label="PropertyTable" size="md" appearance="horizontal">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {!isYou && !isAdmin ? (
                      <IconButton
                        emphasis="bare"
                        icon={<IconEditFilled />}
                        size="sm"
                        onClick={onEditPermissions}
                        title="Edit"
                        css={{ marginInlineEnd: 200 }}
                      />
                    ) : (
                      <></>
                    )}
                    {`${currentUser.userName}'s Permissions:`}
                  </Box>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {makePermissionTableOptions(displayedPermissions).map(([option, permission]) => (
                <Table.Row key={permission}>{option}</Table.Row>
              ))}
              <Table.Row>{applicationPermissionOption}</Table.Row>
            </Table.Body>
          </Table.Root>
        </Col>
      </Row>
      {isInvitation && (
        <>
          <HR />
          <div className="Questions-footer">
            <TextButton className="Questions-button" onClick={onCancel} data-eventid={EVENT_IDS.CANCEL_ADDING_NEW_USER}>
              Cancel
            </TextButton>
            {isInvitation && (
              <div>
                <TextButton className="Questions-button" onClick={onBack}>
                  Back
                </TextButton>
                <Button tone="brand" emphasis="filled" onClick={onSendInvite}>
                  Send Invitation
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {error && (
        <Row hidden={[]}>
          <Col md={12} lg={6}>
            <Alert tone="critical">{error}</Alert>
          </Col>
        </Row>
      )}
      {!isYou && !isInvitation && !isPending && (
        <>
          <Paragraph css={{ marginBlockStart: 'sm' }}>
            {`Do you want to remove ${currentUser.userName} from the company account
                        and revoke their access to properties and features?`}
          </Paragraph>
          <Button css={{ marginBlockStart: 'sm' }} onClick={handleDeactivateAccount} disabled={isApplicationManager}>
            Deactivate Account
          </Button>
        </>
      )}
      {confirmationModalData.display && (
        <ConfirmationModal
          textContent={confirmationModalData.textContent}
          onSubmit={confirmationModalData.onSubmit}
          header={confirmationModalData.header}
          onClose={() => setConfirmationModalData({ ...confirmationModalData, display: false })}
        />
      )}
    </div>
  );
};

export default Preview;
